import type { Skill } from '../skillsData';

// ===== ai-crypto directory – skills missing from cryptoskillAiCrypto.ts =====
// Covers all remaining ~78 folders found in:
// https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto

export const cryptoskillAiCryptoExtraData: Skill[] = [

  // ── Reputation / Identity agents ─────────────────────────
  {
    id: 'ack-reputation',
    name: 'ack-reputation',
    author: 'ACK Protocol',
    authorHandle: '@ack_protocol',
    tag: 'Skill',
    category: 'identity',
    description: 'ACK on-chain reputation scoring agent. Build portable, verifiable reputation scores based on wallet activity, DeFi history, and social attestations.',
    downloads: 8400,
    stars: 420,
    version: '1.0.0',
    installCommand: '> install the ack-reputation skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/ack-reputation',
    usageExample: '> score wallet reputation\n> verify on-chain history\n> export reputation NFT',
    detailedDescription: 'ACK Protocol reputation agent computing on-chain reputation scores from DeFi activity, governance participation, NFT history, and peer attestations into a portable verifiable credential.',
  },
  {
    id: 'agent8-reputation',
    name: 'agent8-reputation',
    author: 'Agent8',
    authorHandle: '@agent8_ai',
    tag: 'Skill',
    category: 'identity',
    description: 'Agent8 AI reputation layer. Trust scoring for autonomous agents — track performance history, past task completions, and penalty records on-chain.',
    downloads: 7600,
    stars: 380,
    version: '1.0.0',
    installCommand: '> install the agent8-reputation skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/agent8-reputation',
    usageExample: '> check agent reputation\n> submit task completion\n> query trust score',
    detailedDescription: 'Agent8 reputation protocol for AI agents. Track autonomous agent performance history, task success rates, and dispute records on-chain to enable trustless agent-to-agent hiring.',
  },
  {
    id: 'agent8',
    name: 'agent8',
    author: 'Agent8',
    authorHandle: '@agent8_ai',
    tag: 'Skill',
    category: 'tools',
    description: 'Agent8 autonomous agent SDK. Build, deploy, and monetize AI agents with built-in payment rails, task queuing, and reputation tracking.',
    downloads: 13200,
    stars: 660,
    version: '1.2.0',
    installCommand: '> install the agent8 skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/agent8',
    usageExample: '> create agent8 agent\n> queue agent task\n> monetize agent service',
    detailedDescription: 'Agent8 full SDK for creating monetizable autonomous agents with native crypto payment rails, priority task queuing, SLA enforcement, and reputation scoring on Base chain.',
    trending: true,
  },

  // ── Creative / Media agents ──────────────────────────────
  {
    id: 'artsonar-creative-agent',
    name: 'artsonar-creative-agent',
    author: 'ArtSonar',
    authorHandle: '@artsonar',
    tag: 'Skill',
    category: 'tools',
    description: 'ArtSonar AI creative agent. Generate, evaluate, and tokenize digital art with on-chain provenance tracking and royalty configuration on NFT marketplaces.',
    downloads: 9800,
    stars: 490,
    version: '1.0.0',
    installCommand: '> install the artsonar-creative-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/artsonar-creative-agent',
    usageExample: '> generate NFT artwork\n> evaluate art rarity\n> configure royalties',
    detailedDescription: 'ArtSonar creative AI agent for generating, evaluating, and minting digital art. Supports on-chain provenance, configurable royalties, multi-marketplace listing, and AI-driven rarity scoring.',
  },

  // ── Heurist / AI queries ─────────────────────────────────
  {
    id: 'ask-heurist',
    name: 'ask-heurist',
    author: 'Heurist AI',
    authorHandle: '@heurist_ai',
    tag: 'Skill',
    category: 'tools',
    description: 'Ask Heurist – natural language crypto AI assistant. Get market insights, DeFi analytics, and on-chain research answers via decentralized LLM inference.',
    downloads: 16400,
    stars: 820,
    version: '1.0.0',
    installCommand: '> install the ask-heurist skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/ask-heurist',
    usageExample: '> ask crypto question\n> get market insight\n> research token',
    detailedDescription: 'Heurist conversational AI for crypto research using decentralized inference. Ask market questions, get DeFi protocol summaries, analyze token fundamentals, and generate research reports.',
  },

  // ── Trust oracles ────────────────────────────────────────
  {
    id: 'bardiel-trust-oracle',
    name: 'bardiel-trust-oracle',
    author: 'Bardiel Protocol',
    authorHandle: '@bardiel_proto',
    tag: 'Skill',
    category: 'identity',
    description: 'Bardiel trust oracle. Decentralized cross-chain trust verification for AI agents — validate agent identities, credentials, and service agreements on-chain.',
    downloads: 6200,
    stars: 310,
    version: '1.0.0',
    installCommand: '> install the bardiel-trust-oracle skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/bardiel-trust-oracle',
    usageExample: '> verify agent identity\n> validate credentials\n> anchor trust proof',
    detailedDescription: 'Bardiel decentralized oracle for agent trust verification. Validate AI agent identities and service credentials across chains using threshold attestation and zero-knowledge proofs.',
  },
  {
    id: 'baseoracle',
    name: 'baseoracle',
    author: 'BaseOracle',
    authorHandle: '@baseoracle',
    tag: 'Skill',
    category: 'tools',
    description: 'BaseOracle real-time data feeds on Base L2. Aggregate off-chain data and push verified price and event feeds to smart contracts on Base.',
    downloads: 11800,
    stars: 590,
    version: '1.0.0',
    installCommand: '> install the baseoracle skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/baseoracle',
    usageExample: '> push data feed\n> subscribe price oracle\n> verify event feed',
    detailedDescription: 'BaseOracle data oracle on Base L2. Aggregate, validate, and publish off-chain price feeds, economic indicators, and custom event data to on-chain consumers with threshold-signed proofs.',
  },

  // ── Billions ecosystem ───────────────────────────────────
  {
    id: 'billions-architect',
    name: 'billions-architect',
    author: 'Billions Protocol',
    authorHandle: '@billions_proto',
    tag: 'Skill',
    category: 'tools',
    description: 'Billions Architect — design and orchestrate multi-agent pipelines on decentralized compute. Define agent DAGs, resource allocation, and consensus rules.',
    downloads: 5800,
    stars: 290,
    version: '1.0.0',
    installCommand: '> install the billions-architect skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/billions-architect',
    usageExample: '> design agent pipeline\n> allocate compute resources\n> set consensus rules',
    detailedDescription: 'Billions Architect framework for orchestrating multi-agent workflows on decentralized compute infrastructure. Design DAG-based pipelines, manage resource allocation, and enforce consensus rules between agents.',
  },

  // ── CamScanner AI ────────────────────────────────────────
  {
    id: 'camscanner-ai-tools',
    name: 'camscanner-ai-tools',
    author: 'CamScanner',
    authorHandle: '@camscanner',
    tag: 'Skill',
    category: 'tools',
    description: 'CamScanner AI document tools for Web3. Extract, verify, and tokenize document data — contracts, invoices, and KYC documents — with on-chain anchoring.',
    downloads: 7200,
    stars: 360,
    version: '1.0.0',
    installCommand: '> install the camscanner-ai-tools skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/camscanner-ai-tools',
    usageExample: '> scan contract document\n> extract KYC data\n> anchor doc hash on-chain',
    detailedDescription: 'CamScanner AI tools adapted for Web3 document workflows. Extract structured data from contracts and invoices, generate cryptographic hashes, and anchor document proofs on-chain for verifiable records.',
  },

  // ── Clarity Protocol ────────────────────────────────────
  {
    id: 'clarity-protocol-node',
    name: 'clarity-protocol-node',
    author: 'Clarity Protocol',
    authorHandle: '@clarity_proto',
    tag: 'Skill',
    category: 'tools',
    description: 'Clarity Protocol node client. Connect to Clarity decentralized AI governance network — vote on model parameters, stake clarity tokens, and run oracle tasks.',
    downloads: 5400,
    stars: 270,
    version: '0.9.0',
    installCommand: '> install the clarity-protocol-node skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/clarity-protocol-node',
    usageExample: '> join clarity network\n> vote model params\n> run oracle task',
    detailedDescription: 'Clarity Protocol node client for participating in decentralized AI governance. Vote on model update proposals, stake CLRT tokens, and run oracle tasks to earn rewards in the governance network.',
  },

  // ── Clawdia ecosystem ────────────────────────────────────
  {
    id: 'clawdia-agent',
    name: 'clawdia-agent',
    author: 'ClawPlaza',
    authorHandle: '@clawplaza',
    tag: 'Skill',
    category: 'tools',
    description: 'Clawdia autonomous AI agent for ClawPlaza. Task execution, inter-agent communication, on-chain payment settlement, and skill marketplace integration.',
    downloads: 9200,
    stars: 460,
    version: '1.1.0',
    installCommand: '> install the clawdia-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/clawdia-agent',
    usageExample: '> launch clawdia agent\n> post task to marketplace\n> settle agent payment',
    detailedDescription: 'Clawdia base agent framework for the ClawPlaza AI marketplace. Supports task posting, peer agent negotiation, skill discovery, crypto payment settlement, and reputation staking.',
    trending: true,
  },
  {
    id: 'clawdia',
    name: 'clawdia',
    author: 'ClawPlaza',
    authorHandle: '@clawplaza',
    tag: 'Skill',
    category: 'tools',
    description: 'Clawdia AI toolkit core. Foundation SDK for building ClawPlaza-compatible agents with crypto wallets, task boards, and decentralized identity.',
    downloads: 12400,
    stars: 620,
    version: '1.0.0',
    installCommand: '> install the clawdia skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/clawdia',
    usageExample: '> init clawdia wallet\n> browse task board\n> create agent identity',
    detailedDescription: 'Clawdia core SDK providing wallet management, on-chain identity, task marketplace access, and agent-to-agent communication primitives for the ClawPlaza decentralized AI ecosystem.',
  },
  {
    id: 'clawdia-clawplaza',
    name: 'clawdia-clawplaza',
    author: 'ClawPlaza',
    authorHandle: '@clawplaza',
    tag: 'Skill',
    category: 'tools',
    description: 'ClawPlaza marketplace integration. List AI services, browse agent tasks, bid on jobs, and manage escrow-secured service agreements.',
    downloads: 8600,
    stars: 430,
    version: '1.0.0',
    installCommand: '> install the clawdia-clawplaza skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/clawdia-clawplaza',
    usageExample: '> list ai service\n> bid on job\n> release escrow',
    detailedDescription: 'ClawPlaza marketplace SDK for listing AI services, discovering agent tasks, bidding via smart contract auctions, and releasing escrow payments upon task completion verification.',
  },
  {
    id: 'clawdia-manager',
    name: 'clawdia-manager',
    author: 'ClawPlaza',
    authorHandle: '@clawplaza',
    tag: 'Skill',
    category: 'tools',
    description: 'Clawdia Manager — orchestrate teams of Clawdia agents. Assign tasks, monitor progress, handle exceptions, and aggregate outputs from parallel agent workers.',
    downloads: 6800,
    stars: 340,
    version: '1.0.0',
    installCommand: '> install the clawdia-manager skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/clawdia-manager',
    usageExample: '> manage agent team\n> assign parallel tasks\n> aggregate results',
    detailedDescription: 'Clawdia Manager orchestration layer for coordinating parallel agent teams. Handles task assignment, real-time progress monitoring, exception escalation, and result aggregation with on-chain audit trails.',
  },
  {
    id: 'clawdia-orchestrator',
    name: 'clawdia-orchestrator',
    author: 'ClawPlaza',
    authorHandle: '@clawplaza',
    tag: 'Skill',
    category: 'tools',
    description: 'Clawdia Orchestrator — high-level workflow planner for complex multi-step agent operations. Decomposes goals into sub-tasks and coordinates specialized sub-agents.',
    downloads: 7400,
    stars: 370,
    version: '1.0.0',
    installCommand: '> install the clawdia-orchestrator skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/clawdia-orchestrator',
    usageExample: '> plan complex workflow\n> decompose goal\n> coordinate sub-agents',
    detailedDescription: 'Clawdia Orchestrator high-level planner that decomposes complex goals into sub-tasks, routes them to specialized agent workers, and synthesizes outputs into coherent final deliverables.',
  },
  {
    id: 'clawdmint',
    name: 'clawdmint',
    author: 'ClawPlaza',
    authorHandle: '@clawplaza',
    tag: 'Skill',
    category: 'tools',
    description: 'ClawdMint — AI-assisted NFT minting platform. Generate, customize, batch-mint, and deploy NFT collections with automated metadata and trait rarity configuration.',
    downloads: 11200,
    stars: 560,
    version: '1.0.0',
    installCommand: '> install the clawdmint skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/clawdmint',
    usageExample: '> design nft collection\n> batch mint nfts\n> configure rarity traits',
    detailedDescription: 'ClawdMint AI-driven NFT minting tool for generating collection artwork, configuring trait rarity distributions, batch minting with gas optimization, and deploying to EVM and Solana chains.',
  },
  {
    id: 'clawnch',
    name: 'clawnch',
    author: 'ClawPlaza',
    authorHandle: '@clawplaza',
    tag: 'Skill',
    category: 'tools',
    description: 'Clawnch — AI-powered token launch platform. Deploy fair-launch tokens, configure vesting schedules, set up liquidity bootstrapping, and manage community airdrop campaigns.',
    downloads: 14800,
    stars: 740,
    version: '1.0.0',
    installCommand: '> install the clawnch skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/clawnch',
    usageExample: '> launch token fair\n> configure vesting\n> run airdrop campaign',
    detailedDescription: 'Clawnch token launch platform with AI-assisted fair launch design, automated vesting contract deployment, liquidity bootstrapping pool (LBP) setup, and merkle-tree airdrop campaign management.',
    trending: true,
  },
  {
    id: 'clawplaza-manager',
    name: 'clawplaza-manager',
    author: 'ClawPlaza',
    authorHandle: '@clawplaza',
    tag: 'Skill',
    category: 'tools',
    description: 'ClawPlaza platform manager. Admin tools for marketplace operators — manage listings, resolve disputes, configure fee structures, and monitor platform metrics.',
    downloads: 4800,
    stars: 240,
    version: '1.0.0',
    installCommand: '> install the clawplaza-manager skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/clawplaza-manager',
    usageExample: '> manage marketplace\n> resolve dispute\n> configure platform fees',
    detailedDescription: 'ClawPlaza marketplace administration toolkit for operators. Manage service listings, resolve escrow disputes, configure dynamic fee structures, and monitor platform health metrics.',
  },
  {
    id: 'clawplaza-orchestrator',
    name: 'clawplaza-orchestrator',
    author: 'ClawPlaza',
    authorHandle: '@clawplaza',
    tag: 'Skill',
    category: 'tools',
    description: 'ClawPlaza workflow orchestrator. Coordinate end-to-end agent workflows within the ClawPlaza ecosystem — from task sourcing to delivery and payment settlement.',
    downloads: 5600,
    stars: 280,
    version: '1.0.0',
    installCommand: '> install the clawplaza-orchestrator skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/clawplaza-orchestrator',
    usageExample: '> start full workflow\n> track task pipeline\n> settle final payment',
    detailedDescription: 'ClawPlaza end-to-end workflow orchestrator handling task sourcing, agent matching, work delivery verification, and automated crypto payment settlement within the platform ecosystem.',
  },
  {
    id: 'clawplaza-task-marketplace',
    name: 'clawplaza-task-marketplace',
    author: 'ClawPlaza',
    authorHandle: '@clawplaza',
    tag: 'Skill',
    category: 'tools',
    description: 'ClawPlaza Task Marketplace. Browse and post AI agent tasks with crypto bounties, deadline enforcement, and reputation-gated quality assurance.',
    downloads: 9800,
    stars: 490,
    version: '1.0.0',
    installCommand: '> install the clawplaza-task-marketplace skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/clawplaza-task-marketplace',
    usageExample: '> browse agent tasks\n> post task bounty\n> quality check delivery',
    detailedDescription: 'ClawPlaza task marketplace for posting AI agent tasks with crypto bounties, enforcing deadlines via smart contracts, and applying reputation-gated quality review before payment release.',
  },

  // ── Convrgent / Intelligence agents ─────────────────────
  {
    id: 'convrgent-intelligence',
    name: 'convrgent-intelligence',
    author: 'Convrgent',
    authorHandle: '@convrgent_ai',
    tag: 'Skill',
    category: 'analytics',
    description: 'Convrgent multi-source intelligence platform. Synthesize on-chain signals, social sentiment, macro indicators, and technical analysis into actionable alpha.',
    downloads: 13600,
    stars: 680,
    version: '1.0.0',
    installCommand: '> install the convrgent-intelligence skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/convrgent-intelligence',
    usageExample: '> synthesize market signals\n> generate alpha report\n> correlate macro data',
    detailedDescription: 'Convrgent intelligence engine synthesizing on-chain wallet flows, social sentiment scores, macro economic indicators, and technical analysis signals into structured alpha generation reports.',
  },

  // ── Corgent / Cortensor ecosystem ───────────────────────
  {
    id: 'corgent-cortensor',
    name: 'corgent-cortensor',
    author: 'Corgent / Cortensor',
    authorHandle: '@corgent_ai',
    tag: 'Skill',
    category: 'tools',
    description: 'Corgent Cortensor decentralized AI compute. Distribute model inference across Cortensor GPU nodes, verify results with PoUW, and pay per inference.',
    downloads: 8800,
    stars: 440,
    version: '1.0.0',
    installCommand: '> install the corgent-cortensor skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/corgent-cortensor',
    usageExample: '> run decentralized inference\n> verify compute proof\n> pay per inference',
    detailedDescription: 'Corgent integration with Cortensor decentralized GPU network. Distribute AI inference tasks, verify results via Proof-of-Useful-Work consensus, and settle payments in CORT tokens.',
  },
  {
    id: 'corgent-trust-oracle',
    name: 'corgent-trust-oracle',
    author: 'Corgent',
    authorHandle: '@corgent_ai',
    tag: 'Skill',
    category: 'identity',
    description: 'Corgent trust oracle. Cross-chain trust scoring for AI agents using Proof-of-Useful-Work attestations and historical performance records.',
    downloads: 5200,
    stars: 260,
    version: '1.0.0',
    installCommand: '> install the corgent-trust-oracle skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/corgent-trust-oracle',
    usageExample: '> score agent trust\n> verify PoUW record\n> query trust history',
    detailedDescription: 'Corgent trust oracle computing cross-chain reputation scores for AI agents based on Proof-of-Useful-Work contributions, task completion history, and staked collateral.',
  },
  {
    id: 'corgent',
    name: 'corgent',
    author: 'Corgent',
    authorHandle: '@corgent_ai',
    tag: 'Skill',
    category: 'tools',
    description: 'Corgent agent framework. Deploy verifiable AI agents on decentralized compute with on-chain action logging, staking-based accountability, and task orchestration.',
    downloads: 10400,
    stars: 520,
    version: '1.0.0',
    installCommand: '> install the corgent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/corgent',
    usageExample: '> deploy corgent agent\n> log on-chain action\n> stake for accountability',
    detailedDescription: 'Corgent framework for deploying accountability-focused AI agents with mandatory on-chain action logging, stake-based commitment, and verifiable task orchestration on decentralized infrastructure.',
  },

  // ── Crypto Claude / news agents ─────────────────────────
  {
    id: 'crypto-claude-skills',
    name: 'crypto-claude-skills',
    author: 'Crypto Claude Community',
    authorHandle: '@crypto_claude',
    tag: 'Skill',
    category: 'tools',
    description: 'Community-built Claude skill pack for crypto. Curated prompts and tools for on-chain research, DeFi strategies, NFT analysis, and market commentary.',
    downloads: 22400,
    stars: 1120,
    version: '2.0.0',
    installCommand: '> install the crypto-claude-skills skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/crypto-claude-skills',
    usageExample: '> research token\n> analyze DeFi protocol\n> write market commentary',
    detailedDescription: 'Community collection of Claude skills optimized for crypto workflows. Includes curated prompt templates, tool integrations, and workflow examples for token research, DeFi strategy, and market analysis.',
    featured: true,
  },
  {
    id: 'crypto-news-agent',
    name: 'crypto-news-agent',
    author: 'Crypto News AI',
    authorHandle: '@cryptonews_ai',
    tag: 'Skill',
    category: 'analytics',
    description: 'Crypto news aggregation and analysis agent. Monitor 200+ crypto news sources, extract key events, assess price impact probability, and deliver smart summaries.',
    downloads: 31800,
    stars: 1590,
    version: '1.0.0',
    installCommand: '> install the crypto-news-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/crypto-news-agent',
    usageExample: '> get latest crypto news\n> analyze event impact\n> monitor key sources',
    detailedDescription: 'AI-powered crypto news aggregator monitoring 200+ sources. Extracts market-moving events, assesses potential price impact with ML scoring, and delivers concise structured summaries for agent decision-making.',
    trending: true,
  },

  // ── Cybercentry security ─────────────────────────────────
  {
    id: 'cybercentry',
    name: 'cybercentry',
    author: 'CyberCentry',
    authorHandle: '@cybercentry',
    tag: 'Skill',
    category: 'tools',
    description: 'CyberCentry blockchain security agent. Continuous smart contract monitoring, anomaly detection, exploit pattern matching, and real-time incident alerting.',
    downloads: 9600,
    stars: 480,
    version: '1.0.0',
    installCommand: '> install the cybercentry skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/cybercentry',
    usageExample: '> monitor contract security\n> detect anomaly\n> alert on exploit pattern',
    detailedDescription: 'CyberCentry security intelligence agent with continuous smart contract monitoring, known exploit pattern matching, abnormal transaction detection, and sub-second incident alerting with remediation guidance.',
  },

  // ── Dexter AI trading ────────────────────────────────────
  {
    id: 'dexter-ai',
    name: 'dexter-ai',
    author: 'Dexter AI',
    authorHandle: '@dexter_ai_',
    tag: 'Skill',
    category: 'trading',
    description: 'Dexter AI autonomous trading agent. Execute algorithmic strategies across CEX and DEX with natural language strategy configuration and risk guardrails.',
    downloads: 17600,
    stars: 880,
    version: '1.5.0',
    installCommand: '> install the dexter-ai skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/dexter-ai',
    usageExample: '> configure trading strategy\n> run backtest\n> execute live trades',
    detailedDescription: 'Dexter AI fully autonomous trading agent supporting natural language strategy configuration, quantitative backtesting, live execution across 20+ exchanges, and configurable risk guardrails.',
    trending: true,
  },

  // ── DGrid gaming agent ───────────────────────────────────
  {
    id: 'dgrid-arena-agent',
    name: 'dgrid-arena-agent',
    author: 'DGrid',
    authorHandle: '@dgrid_io',
    tag: 'Skill',
    category: 'tools',
    description: 'DGrid Arena AI agent. Compete in decentralized gaming arenas, manage in-game assets, optimize strategies with reinforcement learning, and earn crypto rewards.',
    downloads: 12800,
    stars: 640,
    version: '1.0.0',
    installCommand: '> install the dgrid-arena-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/dgrid-arena-agent',
    usageExample: '> join arena battle\n> optimize game strategy\n> manage in-game nfts',
    detailedDescription: 'DGrid Arena AI agent competing in decentralized on-chain gaming. Uses reinforcement learning to optimize strategies, manages NFT in-game assets, and earns tokenized rewards from arena victories.',
  },

  // ── Doppel world builder ─────────────────────────────────
  {
    id: 'doppel-world-builder',
    name: 'doppel-world-builder',
    author: 'Doppel',
    authorHandle: '@doppel_world',
    tag: 'Skill',
    category: 'tools',
    description: 'Doppel World Builder — create AI-powered virtual environments. Build, simulate, and monetize persistent on-chain worlds with agent-driven economies.',
    downloads: 8200,
    stars: 410,
    version: '1.0.0',
    installCommand: '> install the doppel-world-builder skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/doppel-world-builder',
    usageExample: '> build virtual world\n> simulate agent economy\n> monetize world assets',
    detailedDescription: 'Doppel World Builder for creating persistent AI-driven virtual environments. Design spatial layouts, simulate agent-driven micro-economies, issue in-world NFT assets, and earn from world monetization.',
  },

  // ── ElizaOS cloud / OS plugin ────────────────────────────
  {
    id: 'elizacloud',
    name: 'elizacloud',
    author: 'ElizaOS',
    authorHandle: '@elizaos',
    tag: 'Skill',
    category: 'tools',
    description: 'ElizaCloud hosted agent platform. Deploy ElizaOS agents to managed cloud infrastructure with auto-scaling, persistent memory, and monitoring dashboards.',
    downloads: 14800,
    stars: 740,
    version: '1.0.0',
    installCommand: '> install the elizacloud skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/elizacloud',
    usageExample: '> deploy eliza to cloud\n> scale agent instances\n> monitor agent health',
    detailedDescription: 'ElizaCloud managed hosting for ElizaOS agents. One-click deployment with auto-scaling, persistent memory storage, real-time monitoring dashboards, and custom domain support.',
  },
  {
    id: 'elizaos-plugin-defi',
    name: 'elizaos-plugin-defi',
    author: 'ElizaOS',
    authorHandle: '@elizaos',
    tag: 'Skill',
    category: 'defi',
    description: 'ElizaOS DeFi plugin (official v2). Updated DeFi tool suite for ElizaOS agents with improved multi-chain support, gas estimation, and slippage protection.',
    downloads: 18800,
    stars: 940,
    version: '2.0.0',
    installCommand: '> install the elizaos-plugin-defi skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/elizaos-plugin-defi',
    usageExample: '> use elizaos defi v2\n> estimate gas costs\n> set slippage tolerance',
    detailedDescription: 'Official ElizaOS DeFi plugin v2 with enhanced multi-chain compatibility, accurate gas estimation, automatic slippage protection, MEV resistance, and unified DeFi interaction interface.',
  },

  // ── Emblem agent skills ──────────────────────────────────
  {
    id: 'emblem-official-agent-skills',
    name: 'emblem-official-agent-skills',
    author: 'Emblem Finance',
    authorHandle: '@emblem_vault',
    tag: 'Skill',
    category: 'tools',
    description: 'Emblem Finance official agent skills. Create and manage multi-chain NFT vaults holding mixed-asset portfolios, with AI-assisted curation and valuation.',
    downloads: 10200,
    stars: 510,
    version: '1.0.0',
    installCommand: '> install the emblem-official-agent-skills skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/emblem-official-agent-skills',
    usageExample: '> create nft vault\n> curate asset portfolio\n> value vault contents',
    detailedDescription: 'Emblem Finance toolkit for creating multi-chain NFT vaults containing mixed asset portfolios. AI-assisted portfolio curation, real-time vault valuation, and cross-chain asset wrapping.',
  },

  // ── ERC-8004 agent registry ──────────────────────────────
  {
    id: 'erc-8004-agent-registry',
    name: 'erc-8004-agent-registry',
    author: 'ERC-8004 Working Group',
    authorHandle: '@erc8004',
    tag: 'Skill',
    category: 'identity',
    description: 'ERC-8004 agent registry (hyphenated variant). Register AI agents on the ERC-8004 standard registry with capability manifests and verifiable identity.',
    downloads: 7800,
    stars: 390,
    version: '1.0.0',
    installCommand: '> install the erc-8004-agent-registry skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/erc-8004-agent-registry',
    usageExample: '> register agent ERC-8004\n> publish capability manifest\n> verify agent identity',
    detailedDescription: 'ERC-8004 compliant agent registry for publishing AI agent capability manifests, establishing verifiable on-chain identities, and enabling agent discovery across the decentralized agent ecosystem.',
  },
  {
    id: 'erc-8004-registry',
    name: 'erc-8004-registry',
    author: 'ERC-8004 Working Group',
    authorHandle: '@erc8004',
    tag: 'Skill',
    category: 'identity',
    description: 'ERC-8004 general registry client. Read and write to the ERC-8004 on-chain registry — look up agents, verify capabilities, and resolve agent endpoints.',
    downloads: 6400,
    stars: 320,
    version: '1.0.0',
    installCommand: '> install the erc-8004-registry skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/erc-8004-registry',
    usageExample: '> lookup agent registry\n> verify agent capability\n> resolve agent endpoint',
    detailedDescription: 'ERC-8004 registry client for discovering and verifying AI agents on the standard registry. Look up capability manifests, verify agent identities, and resolve service endpoints.',
  },
  {
    id: 'erc8004-agent-registry',
    name: 'erc8004-agent-registry',
    author: 'ERC-8004 Working Group',
    authorHandle: '@erc8004',
    tag: 'Skill',
    category: 'identity',
    description: 'ERC8004 agent registry (compact variant). Streamlined agent registration with automatic capability detection, ENS-linked identity, and reputation bootstrapping.',
    downloads: 8200,
    stars: 410,
    version: '1.1.0',
    installCommand: '> install the erc8004-agent-registry skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/erc8004-agent-registry',
    usageExample: '> auto-register agent\n> link ens identity\n> bootstrap reputation',
    detailedDescription: 'Compact ERC8004 agent registry with automatic capability introspection, ENS domain linking, and reputation bootstrapping from existing on-chain activity.',
  },
  {
    id: 'erc8004-registry',
    name: 'erc8004-registry',
    author: 'ERC-8004 Working Group',
    authorHandle: '@erc8004',
    tag: 'Skill',
    category: 'identity',
    description: 'ERC8004 registry explorer. Browse the full on-chain agent registry, filter by capability, chain, reputation tier, and service type.',
    downloads: 5800,
    stars: 290,
    version: '1.1.0',
    installCommand: '> install the erc8004-registry skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/erc8004-registry',
    usageExample: '> browse agent registry\n> filter by capability\n> find top-rated agents',
    detailedDescription: 'ERC8004 registry explorer with faceted search by agent capability, supported chains, reputation tier, service categories, and pricing models for agent discovery.',
  },

  // ── Wallet skills ────────────────────────────────────────
  {
    id: 'exme-wallet-skill',
    name: 'exme-wallet-skill',
    author: 'ExMe Wallet',
    authorHandle: '@exme_wallet',
    tag: 'Skill',
    category: 'tools',
    description: 'ExMe smart wallet skill. Programmable wallet operations — batch transactions, gasless execution, multi-sig approvals, and AI-driven spending policies.',
    downloads: 13400,
    stars: 670,
    version: '1.0.0',
    installCommand: '> install the exme-wallet-skill skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/exme-wallet-skill',
    usageExample: '> batch transactions\n> execute gasless tx\n> set spending policy',
    detailedDescription: 'ExMe smart wallet integration enabling batch transactions, ERC-4337 gasless execution, multi-signature approval flows, and AI-configured programmable spending policies.',
  },
  {
    id: 'exton-wallet-skill',
    name: 'exton-wallet-skill',
    author: 'Exton Wallet',
    authorHandle: '@exton_wallet',
    tag: 'Skill',
    category: 'tools',
    description: 'Exton secure wallet skill. Hardware-backed MPC wallets with biometric authorization, multi-chain support, and automatic DeFi position management.',
    downloads: 11600,
    stars: 580,
    version: '1.0.0',
    installCommand: '> install the exton-wallet-skill skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/exton-wallet-skill',
    usageExample: '> create secure wallet\n> authorize with biometric\n> manage DeFi positions',
    detailedDescription: 'Exton hardware-backed MPC wallet with biometric authorization, multi-chain asset management, automated DeFi position rebalancing, and insurance-grade key recovery.',
  },

  // ── FX / trading tools ───────────────────────────────────
  {
    id: 'fx-mcp',
    name: 'fx-mcp',
    author: 'FX Protocol',
    authorHandle: '@fx_protocol',
    tag: 'Skill',
    category: 'trading',
    description: 'FX-MCP forex and crypto cross-rate trading. Access FX rates, execute cross-border settlements, and hedge crypto/fiat exposure via Model Context Protocol.',
    downloads: 9200,
    stars: 460,
    version: '1.0.0',
    installCommand: '> install the fx-mcp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/fx-mcp',
    usageExample: '> get FX cross rates\n> settle cross-border payment\n> hedge fiat exposure',
    detailedDescription: 'FX-MCP server bridging traditional foreign exchange rates with crypto settlements. Access real-time FX rates, execute cross-border USDC/stablecoin settlements, and manage crypto-fiat hedging.',
  },

  // ── Hive SDK ─────────────────────────────────────────────
  {
    id: 'hive-sdk',
    name: 'hive-sdk',
    author: 'Hive Blockchain',
    authorHandle: '@hive_blockchain',
    tag: 'Skill',
    category: 'tools',
    description: 'Hive Blockchain SDK for AI agents. Interact with the Hive social blockchain — post content, earn HIVE rewards, manage RC credits, and access community data.',
    downloads: 8600,
    stars: 430,
    version: '1.0.0',
    installCommand: '> install the hive-sdk skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/hive-sdk',
    usageExample: '> post to hive blockchain\n> earn hive rewards\n> manage RC credits',
    detailedDescription: 'Hive Blockchain SDK enabling AI agents to post content, curate community posts, earn HIVE/HBD rewards, manage resource credit delegation, and access community voting data.',
  },

  // ── Jake concierge ───────────────────────────────────────
  {
    id: 'jake-concierge',
    name: 'jake-concierge',
    author: 'Jake AI',
    authorHandle: '@jake_ai',
    tag: 'Skill',
    category: 'tools',
    description: 'Jake AI concierge agent. Personal crypto assistant with portfolio insights, DeFi recommendations, tax summaries, and calendar-integrated transaction reminders.',
    downloads: 16800,
    stars: 840,
    version: '1.0.0',
    installCommand: '> install the jake-concierge skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/jake-concierge',
    usageExample: '> get portfolio summary\n> recommend defi action\n> set tx reminder',
    detailedDescription: 'Jake AI personal crypto concierge providing portfolio insights, proactive DeFi opportunity recommendations, automated tax summaries, and calendar-integrated transaction reminders.',
  },

  // ── Loopuman human layer ─────────────────────────────────
  {
    id: 'loopuman-human-layer',
    name: 'loopuman-human-layer',
    author: 'LoopuMan',
    authorHandle: '@loopuman',
    tag: 'Skill',
    category: 'tools',
    description: 'LoopuMan Human-in-the-Loop layer. Add human approval checkpoints to autonomous agent workflows — review sensitive actions before on-chain execution.',
    downloads: 11200,
    stars: 560,
    version: '1.0.0',
    installCommand: '> install the loopuman-human-layer skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/loopuman-human-layer',
    usageExample: '> add approval checkpoint\n> review agent action\n> approve or reject tx',
    detailedDescription: 'LoopuMan human oversight layer inserting configurable approval checkpoints into autonomous agent workflows. Route sensitive actions to human reviewers before on-chain execution with timeout fallbacks.',
    trending: true,
  },
  {
    id: 'loopuman',
    name: 'loopuman',
    author: 'LoopuMan',
    authorHandle: '@loopuman',
    tag: 'Skill',
    category: 'tools',
    description: 'LoopuMan agent loop manager. Build iterative agent workflows with feedback loops, self-correction, persistent state, and configurable iteration budgets.',
    downloads: 9600,
    stars: 480,
    version: '1.0.0',
    installCommand: '> install the loopuman skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/loopuman',
    usageExample: '> build agent loop\n> add self-correction\n> set iteration budget',
    detailedDescription: 'LoopuMan framework for building iterative agent workflows with automatic self-correction, persistent cross-iteration state, performance tracking, and configurable iteration budgets.',
  },

  // ── M2M toolbox ──────────────────────────────────────────
  {
    id: 'm2m-toolbox',
    name: 'm2m-toolbox',
    author: 'M2M Protocol',
    authorHandle: '@m2m_protocol',
    tag: 'Skill',
    category: 'tools',
    description: 'Machine-to-Machine (M2M) crypto payment toolbox. Micropayment channels, streaming payments, metered API billing, and autonomous agent treasury management.',
    downloads: 12800,
    stars: 640,
    version: '1.0.0',
    installCommand: '> install the m2m-toolbox skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/m2m-toolbox',
    usageExample: '> open payment channel\n> stream micropayments\n> meter api billing',
    detailedDescription: 'M2M payment toolbox for autonomous agent economies. Create payment channels, stream real-time micropayments, implement metered API billing, and manage agent treasury balances.',
  },

  // ── MCP servers ──────────────────────────────────────────
  {
    id: 'mcp-servers-ai',
    name: 'mcp-servers',
    author: 'MCP Community',
    authorHandle: '@mcp_community',
    tag: 'Skill',
    category: 'tools',
    description: 'Community MCP servers collection. Curated registry of Model Context Protocol servers for crypto, DeFi, trading, and Web3 tools integration.',
    downloads: 26400,
    stars: 1320,
    version: '1.0.0',
    installCommand: '> install the mcp-servers skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/mcp-servers',
    usageExample: '> browse mcp servers\n> connect mcp endpoint\n> discover web3 tools',
    detailedDescription: 'Curated registry of community-built MCP servers for crypto and Web3. Browse, connect, and integrate 100+ MCP-compatible tool servers for DeFi, trading, analytics, and blockchain operations.',
    featured: true,
  },

  // ── Memora / Olas memory ─────────────────────────────────
  {
    id: 'memora-olas',
    name: 'memora-olas',
    author: 'Memora / Valory',
    authorHandle: '@valory_xyz',
    tag: 'Skill',
    category: 'tools',
    description: 'Memora on Olas – persistent agent memory layer. Store structured memories, retrieve context across sessions, and share knowledge between Olas agents.',
    downloads: 8200,
    stars: 410,
    version: '1.0.0',
    installCommand: '> install the memora-olas skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/memora-olas',
    usageExample: '> store agent memory\n> retrieve past context\n> share agent knowledge',
    detailedDescription: 'Memora persistent memory layer built on Olas infrastructure. Store structured agent memories, retrieve historical context for improved decision-making, and share knowledge graphs between collaborating agents.',
  },

  // ── Minara additional variants ───────────────────────────
  {
    id: 'minara-ai-assistant',
    name: 'minara-ai-assistant',
    author: 'Minara AI',
    authorHandle: '@minara_ai',
    tag: 'Skill',
    category: 'tools',
    description: 'Minara AI assistant (base edition). General-purpose AI assistant with crypto market context, wallet integrations, and DeFi protocol awareness.',
    downloads: 14600,
    stars: 730,
    version: '1.0.0',
    installCommand: '> install the minara-ai-assistant skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/minara-ai-assistant',
    usageExample: '> ask minara assistant\n> check wallet balance\n> explore DeFi options',
    detailedDescription: 'Minara AI base assistant providing general crypto intelligence with wallet integration, market awareness, and DeFi protocol knowledge for everyday crypto user assistance.',
  },
  {
    id: 'minara-ai-crypto-assistant',
    name: 'minara-ai-crypto-assistant',
    author: 'Minara AI',
    authorHandle: '@minara_ai',
    tag: 'Skill',
    category: 'tools',
    description: 'Minara AI crypto assistant (pro edition). Deep crypto analysis, on-chain forensics, tax optimization hints, and automated DeFi rebalancing suggestions.',
    downloads: 11800,
    stars: 590,
    version: '1.5.0',
    installCommand: '> install the minara-ai-crypto-assistant skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/minara-ai-crypto-assistant',
    usageExample: '> deep token analysis\n> trace on-chain activity\n> suggest rebalancing',
    detailedDescription: 'Minara AI pro crypto assistant with deep on-chain analysis capabilities, forensic tracing tools, tax event detection, and automated DeFi portfolio rebalancing recommendations.',
  },
  {
    id: 'minara-crypto-assistant',
    name: 'minara-crypto-assistant',
    author: 'Minara AI',
    authorHandle: '@minara_ai',
    tag: 'Skill',
    category: 'tools',
    description: 'Minara crypto assistant (lite edition). Fast, lightweight crypto Q&A, price lookups, and basic portfolio summaries with minimal API calls.',
    downloads: 9200,
    stars: 460,
    version: '1.0.0',
    installCommand: '> install the minara-crypto-assistant skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/minara-crypto-assistant',
    usageExample: '> quick price lookup\n> get portfolio summary\n> ask crypto question',
    detailedDescription: 'Minara lightweight crypto assistant optimized for speed and minimal API usage. Instant price lookups, basic portfolio snapshots, and concise crypto Q&A for low-latency agent integrations.',
  },

  // ── Molty cash workers ───────────────────────────────────
  {
    id: 'molty-cash-workers',
    name: 'molty-cash-workers',
    author: 'Molty',
    authorHandle: '@molty_io',
    tag: 'Skill',
    category: 'tools',
    description: 'Molty cash workers — micro-task crypto payment workers. Deploy swarms of small agents for parallel data collection, labeling, and verification with instant USDC payouts.',
    downloads: 7400,
    stars: 370,
    version: '1.0.0',
    installCommand: '> install the molty-cash-workers skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/molty-cash-workers',
    usageExample: '> deploy micro-task workers\n> collect data in parallel\n> payout in USDC',
    detailedDescription: 'Molty framework for deploying micro-task agent swarms. Orchestrate parallel data collection, content labeling, and verification tasks with automatic USDC micropayment distribution.',
  },

  // ── Muto AI ──────────────────────────────────────────────
  {
    id: 'muto-ai',
    name: 'muto-ai',
    author: 'Muto AI',
    authorHandle: '@muto_ai',
    tag: 'Skill',
    category: 'tools',
    description: 'Muto AI multi-modal agent. Process text, images, audio, and on-chain data in unified workflows. Ideal for NFT analysis, content moderation, and media tokenization.',
    downloads: 10400,
    stars: 520,
    version: '1.0.0',
    installCommand: '> install the muto-ai skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/muto-ai',
    usageExample: '> analyze nft image\n> moderate content\n> tokenize media',
    detailedDescription: 'Muto AI multi-modal agent processing text, images, audio, and on-chain data in unified pipelines. Supports NFT visual analysis, automated content moderation, and media asset tokenization.',
  },

  // ── Nansen skill ─────────────────────────────────────────
  {
    id: 'nansen-skill',
    name: 'nansen-skill',
    author: 'Nansen',
    authorHandle: '@nansen_ai',
    tag: 'Skill',
    category: 'analytics',
    description: 'Nansen all-in-one skill pack. Combines Smart Money, portfolio analytics, token screener, and holder analysis into a single unified Nansen agent integration.',
    downloads: 28400,
    stars: 1420,
    version: '2.0.0',
    installCommand: '> install the nansen-skill skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/nansen-skill',
    usageExample: '> run full nansen analysis\n> comprehensive wallet report\n> combined token screening',
    detailedDescription: 'Nansen unified skill pack bundling Smart Money tracking, portfolio analytics, token screener, holder analysis, and alert management into a single integrated agent module.',
    featured: true,
  },

  // ── OpenClaw / Virtuals ACP ──────────────────────────────
  {
    id: 'openclaw-virtuals-acp',
    name: 'openclaw-virtuals-acp',
    author: 'OpenClaw',
    authorHandle: '@openclaw_io',
    tag: 'Skill',
    category: 'tools',
    description: 'OpenClaw Virtuals ACP integration. Open-source bridge connecting ClawPlaza agents to the Virtuals Agent Commerce Protocol for cross-ecosystem task trading.',
    downloads: 7200,
    stars: 360,
    version: '1.0.0',
    installCommand: '> install the openclaw-virtuals-acp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/openclaw-virtuals-acp',
    usageExample: '> bridge clawplaza to virtuals\n> trade cross-ecosystem tasks\n> settle ACP payment',
    detailedDescription: 'OpenClaw bridge connecting ClawPlaza agents to Virtuals Protocol ACP. Enables cross-ecosystem task posting, agent discovery, and payment settlement between the two largest on-chain agent marketplaces.',
  },

  // ── OpenMM market making ─────────────────────────────────
  {
    id: 'openmm-mcp',
    name: 'openmm-mcp',
    author: 'OpenMM',
    authorHandle: '@openmm_io',
    tag: 'Skill',
    category: 'trading',
    description: 'OpenMM market making MCP. Deploy automated market making strategies on AMM DEXes via Model Context Protocol with configurable fee tier and rebalancing logic.',
    downloads: 11400,
    stars: 570,
    version: '1.0.0',
    installCommand: '> install the openmm-mcp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/openmm-mcp',
    usageExample: '> deploy LP strategy\n> configure fee tier\n> auto-rebalance range',
    detailedDescription: 'OpenMM automated market making via MCP. Deploy concentrated liquidity positions on Uniswap v3/v4, configure fee tiers, set rebalancing thresholds, and monitor impermanent loss in real time.',
  },

  // ── PowerSun TRON MCP ────────────────────────────────────
  {
    id: 'powersun-tron-mcp',
    name: 'powersun-tron-mcp',
    author: 'PowerSun',
    authorHandle: '@powersun_tron',
    tag: 'Skill',
    category: 'tools',
    description: 'PowerSun TRON MCP server. Full TRON chain interactions — TRX transfers, TRC-20/721 tokens, energy/bandwidth management, and TRON DeFi protocols.',
    downloads: 9800,
    stars: 490,
    version: '1.0.0',
    installCommand: '> install the powersun-tron-mcp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/powersun-tron-mcp',
    usageExample: '> transfer TRX\n> manage energy bandwidth\n> interact tron defi',
    detailedDescription: 'PowerSun MCP server for complete TRON blockchain operations. TRX/TRC transfers, energy and bandwidth resource management, TRON DeFi (JustLend, JustSwap) interactions, and USDT-TRON payments.',
  },

  // ── Privacy layer ────────────────────────────────────────
  {
    id: 'privacy-layer',
    name: 'privacy-layer',
    author: 'Privacy Layer',
    authorHandle: '@privacylayer',
    tag: 'Skill',
    category: 'tools',
    description: 'Privacy Layer for AI agents. Encrypt agent communications, shield transaction amounts, and implement selective disclosure for compliant private on-chain operations.',
    downloads: 10800,
    stars: 540,
    version: '1.0.0',
    installCommand: '> install the privacy-layer skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/privacy-layer',
    usageExample: '> encrypt agent comms\n> shield tx amount\n> selective disclosure',
    detailedDescription: 'Privacy Layer providing agent communication encryption, transaction amount shielding using ZK proofs, and selective disclosure mechanisms for regulatory compliant private on-chain operations.',
  },

  // ── ProfitSpot MCP ───────────────────────────────────────
  {
    id: 'profitspot-mcp',
    name: 'profitspot-mcp',
    author: 'ProfitSpot',
    authorHandle: '@profitspot_io',
    tag: 'Skill',
    category: 'trading',
    description: 'ProfitSpot trading intelligence MCP. AI-driven trade signals, entry/exit timing, position sizing recommendations, and real-time P&L tracking.',
    downloads: 12600,
    stars: 630,
    version: '1.0.0',
    installCommand: '> install the profitspot-mcp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/profitspot-mcp',
    usageExample: '> get trade signal\n> optimize entry timing\n> size position',
    detailedDescription: 'ProfitSpot AI trading intelligence via MCP. Generates entry/exit signals, optimal position sizing based on volatility, real-time P&L attribution, and drawdown monitoring for disciplined trading.',
    trending: true,
  },

  // ── Repute agent ─────────────────────────────────────────
  {
    id: 'repute-agent',
    name: 'repute-agent',
    author: 'Repute Protocol',
    authorHandle: '@repute_proto',
    tag: 'Skill',
    category: 'identity',
    description: 'Repute on-chain reputation agent. Build, maintain, and monetize verifiable reputation scores for wallets and agents using cross-protocol social proof.',
    downloads: 9400,
    stars: 470,
    version: '1.0.0',
    installCommand: '> install the repute-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/repute-agent',
    usageExample: '> build reputation score\n> monetize reputation\n> verify social proof',
    detailedDescription: 'Repute Protocol agent for building and maintaining cross-protocol on-chain reputation. Aggregate activity across DeFi, governance, and social platforms into a portable, monetizable reputation credential.',
  },

  // ── Selantar mediator variants ───────────────────────────
  {
    id: 'selantar-dispute-mediator',
    name: 'selantar-dispute-mediator',
    author: 'Selantar',
    authorHandle: '@selantar',
    tag: 'Skill',
    category: 'tools',
    description: 'Selantar Dispute Mediator. Specialized agent for resolving on-chain commercial disputes with structured evidence submission, neutral mediation, and enforceable rulings.',
    downloads: 5200,
    stars: 260,
    version: '1.0.0',
    installCommand: '> install the selantar-dispute-mediator skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/selantar-dispute-mediator',
    usageExample: '> submit dispute evidence\n> request neutral mediator\n> enforce ruling',
    detailedDescription: 'Selantar specialized dispute mediator for complex on-chain commercial conflicts. Manages evidence submission workflow, assigns vetted neutral mediators, and enforces final rulings via smart contracts.',
  },
  {
    id: 'selantar-mediator',
    name: 'selantar-mediator',
    author: 'Selantar',
    authorHandle: '@selantar',
    tag: 'Skill',
    category: 'tools',
    description: 'Selantar Mediator SDK. Build custom AI mediators for on-chain dispute resolution with configurable evidence standards, mediation protocols, and settlement templates.',
    downloads: 4800,
    stars: 240,
    version: '1.0.0',
    installCommand: '> install the selantar-mediator skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/selantar-mediator',
    usageExample: '> build custom mediator\n> set evidence standards\n> deploy settlement template',
    detailedDescription: 'Selantar Mediator SDK for building domain-specific AI mediators. Configure evidence submission standards, mediation protocol rules, and reusable settlement template libraries for niche dispute categories.',
  },

  // ── Solana / Solscan skills ──────────────────────────────
  {
    id: 'solana-rpc-skill',
    name: 'solana-rpc-skill',
    author: 'Solana Labs',
    authorHandle: '@solanalabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Solana RPC skill. Low-level Solana JSON-RPC wrapper for agents — query accounts, programs, transactions, and subscribe to real-time account/slot updates.',
    downloads: 38400,
    stars: 1920,
    version: '1.0.0',
    installCommand: '> install the solana-rpc-skill skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/solana-rpc-skill',
    usageExample: '> query solana account\n> get program accounts\n> subscribe slot updates',
    detailedDescription: 'Complete Solana RPC skill wrapping all JSON-RPC methods for account queries, transaction fetching, program account filtering, and real-time WebSocket subscriptions for slot/account updates.',
    featured: true,
  },
  {
    id: 'solscan-skill',
    name: 'solscan-skill',
    author: 'Solscan',
    authorHandle: '@solscanofficial',
    tag: 'Skill',
    category: 'analytics',
    description: 'Solscan analytics skill. Solana blockchain explorer API — token balances, NFT portfolios, DeFi activity, and transaction history via Solscan data feeds.',
    downloads: 29600,
    stars: 1480,
    version: '1.0.0',
    installCommand: '> install the solscan-skill skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/solscan-skill',
    usageExample: '> get solana balances\n> view nft portfolio\n> trace sol transactions',
    detailedDescription: 'Solscan API integration providing comprehensive Solana wallet analytics including SPL token balances, NFT portfolio valuation, DeFi position tracking, and detailed transaction history.',
    trending: true,
  },

  // ── SpoonOS official skill ───────────────────────────────
  {
    id: 'spoonos-official-awesome-skill',
    name: 'spoonos-official-awesome-skill',
    author: 'SpoonOS',
    authorHandle: '@spoonos',
    tag: 'Skill',
    category: 'tools',
    description: 'SpoonOS official curated skill collection. Awesome list of production-ready SpoonOS agent skills for DeFi, NFTs, DAO governance, and chain analytics.',
    downloads: 14200,
    stars: 710,
    version: '1.0.0',
    installCommand: '> install the spoonos-official-awesome-skill skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/spoonos-official-awesome-skill',
    usageExample: '> explore spoonos skills\n> install curated pack\n> run spoonos agent',
    detailedDescription: 'SpoonOS official curated collection of production-tested agent skills. Covers DeFi automation, NFT management, DAO governance participation, and multi-chain analytics in one installable pack.',
  },

  // ── Thryx launchpad ─────────────────────────────────────
  {
    id: 'thryx-launchpad',
    name: 'thryx-launchpad',
    author: 'Thryx',
    authorHandle: '@thryx_io',
    tag: 'Skill',
    category: 'tools',
    description: 'Thryx AI-powered token launchpad. Automated IDO/TGE preparation — whitelist management, KYC checks, vesting setup, and post-launch liquidity deployment.',
    downloads: 13400,
    stars: 670,
    version: '1.0.0',
    installCommand: '> install the thryx-launchpad skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/thryx-launchpad',
    usageExample: '> prepare IDO launch\n> manage whitelist\n> deploy post-launch liquidity',
    detailedDescription: 'Thryx comprehensive token launch platform handling whitelist curation, KYC/AML checks, vesting contract deployment, tokenomics validation, and automated post-TGE liquidity provision.',
    trending: true,
  },

  // ── Trade router MCP ────────────────────────────────────
  {
    id: 'trade-router-mcp',
    name: 'trade-router-mcp',
    author: 'Trade Router',
    authorHandle: '@traderouter',
    tag: 'Skill',
    category: 'trading',
    description: 'Trade Router MCP — smart order routing across 30+ DEXes. Find best execution paths, split orders to minimize price impact, and access aggregated liquidity.',
    downloads: 24600,
    stars: 1230,
    version: '1.0.0',
    installCommand: '> install the trade-router-mcp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/trade-router-mcp',
    usageExample: '> find best swap route\n> split large order\n> minimize price impact',
    detailedDescription: 'Trade Router intelligent DEX aggregation via MCP. Scans 30+ DEXes for optimal swap routes, automatically splits large orders to minimize price impact, and selects the cheapest gas path.',
    featured: true,
    trending: true,
  },

  // ── Trust city exchange ──────────────────────────────────
  {
    id: 'trust-city-exchange',
    name: 'trust-city-exchange',
    author: 'Trust City',
    authorHandle: '@trustcity_io',
    tag: 'Skill',
    category: 'tools',
    description: 'Trust City Exchange — decentralized reputation-gated trading. Only trade with counterparties meeting your trust threshold, secured by on-chain reputation scores.',
    downloads: 8600,
    stars: 430,
    version: '1.0.0',
    installCommand: '> install the trust-city-exchange skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/trust-city-exchange',
    usageExample: '> set trust threshold\n> find trusted counterparty\n> execute trusted trade',
    detailedDescription: 'Trust City reputation-gated trading platform. Configure minimum trust score requirements, discover counterparties meeting your criteria, and execute trades with reputation-backed guarantees.',
  },

  // ── Universal crypto skill for agents ───────────────────
  {
    id: 'universal-crypto-skill-for-agents',
    name: 'universal-crypto-skill-for-agents',
    author: 'Universal Crypto',
    authorHandle: '@universal_crypto',
    tag: 'Skill',
    category: 'tools',
    description: 'Universal crypto skill pack for AI agents. Comprehensive bundle covering price data, wallet ops, DeFi, trading, and analytics in one installable module.',
    downloads: 42800,
    stars: 2140,
    version: '2.0.0',
    installCommand: '> install the universal-crypto-skill-for-agents skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/universal-crypto-skill-for-agents',
    usageExample: '> enable all crypto skills\n> unified agent interface\n> full web3 capability',
    detailedDescription: 'All-in-one crypto skill bundle for AI agents. Single install provides price feeds, wallet management, multi-chain DeFi, trading execution, and analytics — the fastest way to add full Web3 capability.',
    featured: true,
    trending: true,
  },

  // ── VaultPilot MCP ───────────────────────────────────────
  {
    id: 'vaultpilot-mcp',
    name: 'vaultpilot-mcp',
    author: 'VaultPilot',
    authorHandle: '@vaultpilot',
    tag: 'Skill',
    category: 'defi',
    description: 'VaultPilot yield vault automation MCP. Automatically move assets between highest-yielding vaults, compound rewards, and rebalance based on risk tolerance.',
    downloads: 16200,
    stars: 810,
    version: '1.0.0',
    installCommand: '> install the vaultpilot-mcp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/vaultpilot-mcp',
    usageExample: '> auto-compound vault rewards\n> migrate to best yield\n> rebalance risk profile',
    detailedDescription: 'VaultPilot automated yield optimization via MCP. Continuously monitors vault APYs, automatically migrates assets to highest-yielding options, compounds rewards, and rebalances based on risk parameters.',
  },

  // ── VerdictSwarm MCP ─────────────────────────────────────
  {
    id: 'verdictswarm-mcp',
    name: 'verdictswarm-mcp',
    author: 'VerdictSwarm',
    authorHandle: '@verdictswarm',
    tag: 'Skill',
    category: 'tools',
    description: 'VerdictSwarm collective decision MCP. Reach swarm consensus among multiple AI agents — voting, deliberation, and binding on-chain resolution for complex decisions.',
    downloads: 7800,
    stars: 390,
    version: '1.0.0',
    installCommand: '> install the verdictswarm-mcp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/verdictswarm-mcp',
    usageExample: '> start swarm vote\n> deliberate decision\n> commit on-chain verdict',
    detailedDescription: 'VerdictSwarm collective intelligence MCP enabling multiple AI agents to deliberate, vote, and reach binding consensus on complex decisions with on-chain commitment and audit trails.',
  },

  // ── Virtual exchange ─────────────────────────────────────
  {
    id: 'virtual-exchange',
    name: 'virtual-exchange',
    author: 'Virtual Exchange',
    authorHandle: '@virtual_exch',
    tag: 'Skill',
    category: 'trading',
    description: 'Virtual Exchange paper trading simulator. Test crypto strategies risk-free with real market data, simulated order books, and performance attribution analytics.',
    downloads: 21400,
    stars: 1070,
    version: '1.0.0',
    installCommand: '> install the virtual-exchange skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/virtual-exchange',
    usageExample: '> start paper trading\n> simulate order book\n> analyze strategy perf',
    detailedDescription: 'Virtual Exchange paper trading environment with real-time market data feeds, realistic order book simulation, slippage modeling, and comprehensive strategy performance attribution for risk-free testing.',
  },

  // ── Virtuals protocol variants ───────────────────────────
  {
    id: 'virtuals-protocol-agent',
    name: 'virtuals-protocol-agent',
    author: 'Virtuals Protocol',
    authorHandle: '@virtuals_io',
    tag: 'Skill',
    category: 'tools',
    description: 'Virtuals Protocol agent SDK. Create a single co-owned AI agent with tokenized revenue sharing, configurable skills, and ACP marketplace participation.',
    downloads: 22600,
    stars: 1130,
    version: '2.0.0',
    installCommand: '> install the virtuals-protocol-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/virtuals-protocol-agent',
    usageExample: '> create virtuals agent\n> configure skill set\n> share agent revenue',
    detailedDescription: 'Virtuals Protocol single-agent SDK for building co-owned AI agents. Configure skill sets, deploy to Base chain, tokenize revenue sharing with VIRTUAL, and list on ACP marketplace.',
  },
  {
    id: 'virtuals-protocol-agents',
    name: 'virtuals-protocol-agents',
    author: 'Virtuals Protocol',
    authorHandle: '@virtuals_io',
    tag: 'Skill',
    category: 'tools',
    description: 'Virtuals Protocol multi-agent SDK. Build and orchestrate teams of co-owned AI agents with shared revenue pools, cross-agent communication, and collective governance.',
    downloads: 18400,
    stars: 920,
    version: '2.0.0',
    installCommand: '> install the virtuals-protocol-agents skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/virtuals-protocol-agents',
    usageExample: '> create agent team\n> share revenue pool\n> govern agent collective',
    detailedDescription: 'Virtuals Protocol multi-agent orchestration SDK for teams of co-owned agents. Manage shared VIRTUAL revenue pools, enable cross-agent communication, and implement collective governance mechanisms.',
  },
  {
    id: 'virtuals-protocol-integration',
    name: 'virtuals-protocol-integration',
    author: 'Virtuals Protocol',
    authorHandle: '@virtuals_io',
    tag: 'Skill',
    category: 'tools',
    description: 'Virtuals Protocol integration kit. Connect existing applications and agents to the Virtuals ecosystem — embed agents in products, track usage, and distribute revenue.',
    downloads: 15600,
    stars: 780,
    version: '2.0.0',
    installCommand: '> install the virtuals-protocol-integration skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/virtuals-protocol-integration',
    usageExample: '> embed virtuals agent\n> track usage metrics\n> distribute revenue',
    detailedDescription: 'Virtuals Protocol integration toolkit for embedding agents into existing products. Usage metering, revenue distribution, white-label agent interfaces, and API gateway for third-party app integration.',
  },
  {
    id: 'virtuals',
    name: 'virtuals',
    author: 'Virtuals Protocol',
    authorHandle: '@virtuals_io',
    tag: 'Skill',
    category: 'tools',
    description: 'Virtuals core library. Low-level primitives for building Virtuals-compatible agent infrastructure — token economics, ACP messaging, and on-chain state management.',
    downloads: 11200,
    stars: 560,
    version: '2.0.0',
    installCommand: '> install the virtuals skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/virtuals',
    usageExample: '> use virtuals primitives\n> implement ACP messaging\n> manage on-chain state',
    detailedDescription: 'Virtuals Protocol core library providing low-level primitives for token economics, ACP protocol messaging, on-chain state management, and composable agent infrastructure building blocks.',
  },

  // ── Web3 airdrop agent ───────────────────────────────────
  {
    id: 'web3-airdrop-agent',
    name: 'web3-airdrop-agent',
    author: 'Web3 Airdrop',
    authorHandle: '@web3airdrop',
    tag: 'Skill',
    category: 'tools',
    description: 'Web3 Airdrop automation agent. Design, execute, and track token airdrop campaigns with merkle-tree distribution, eligibility screening, and anti-sybil detection.',
    downloads: 18800,
    stars: 940,
    version: '1.0.0',
    installCommand: '> install the web3-airdrop-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/web3-airdrop-agent',
    usageExample: '> design airdrop campaign\n> screen eligible wallets\n> detect sybil addresses',
    detailedDescription: 'Web3 Airdrop agent automating end-to-end airdrop campaigns. Define eligibility criteria, generate merkle-tree proofs, deploy distribution contracts, and run ML-based sybil detection to protect fair distribution.',
    trending: true,
  },

  // ── X402 payment / sentiment ─────────────────────────────
  {
    id: 'x402-openrouter-proxy',
    name: 'x402-openrouter-proxy',
    author: 'X402 Protocol',
    authorHandle: '@x402_protocol',
    tag: 'Skill',
    category: 'tools',
    description: 'X402 OpenRouter proxy. Pay-per-use LLM access via X402 HTTP payment protocol. Agents autonomously pay for AI inference with crypto micropayments.',
    downloads: 14200,
    stars: 710,
    version: '1.0.0',
    installCommand: '> install the x402-openrouter-proxy skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/x402-openrouter-proxy',
    usageExample: '> pay per LLM call\n> autonomous micropayment\n> access any openrouter model',
    detailedDescription: 'X402 protocol proxy enabling autonomous crypto micropayments for LLM API access via OpenRouter. Agents self-fund inference costs, enabling truly autonomous AI without human billing intervention.',
    trending: true,
  },
  {
    id: 'x402-token-sentiment-api',
    name: 'x402-token-sentiment-api',
    author: 'X402 Protocol',
    authorHandle: '@x402_protocol',
    tag: 'Skill',
    category: 'analytics',
    description: 'X402 token sentiment API. Pay-per-query sentiment analysis API for crypto tokens — NLP scoring of social posts, news, and community discussions.',
    downloads: 11800,
    stars: 590,
    version: '1.0.0',
    installCommand: '> install the x402-token-sentiment-api skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/ai-crypto/x402-token-sentiment-api',
    usageExample: '> query token sentiment\n> score social posts\n> pay per api call',
    detailedDescription: 'X402 pay-per-query sentiment analysis API for crypto tokens. Analyze social media, news, and community discussions using NLP scoring, with agents autonomously paying for each API query in crypto.',
  },
];